import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

import { ProfileState } from "../../model";
import { getToken } from "../../utils/tokenStorage";
import { url } from "../../utils/config";
import { delayMessage } from "../../utils/delayMessage";
import { serverMessage } from "../../utils/serverMessage";

const initialState: ProfileState = {
  profileLoading: false,
  userProfile: {
    _id: "",
    location: "",
  },
  pendingRequestTimer: null,
};

// Function to get each user data from the firestore
export const getUserProfile = createAsyncThunk(
  "data/getdata",
  async (_, thunkAPI) => {
    try {
      const token = getToken();
      const { data } = await axios.get(`${url.baseUrl}/profile`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return data;
    } catch (error: any) {
      console.log(error.response);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

// Function to update data in the firestore
export const updateProfile = createAsyncThunk(
  "profile/updateProfile",
  async (userProfile: any, thunkAPI) => {
    const { location, _id, dispatch } = userProfile;
    try {
      const token = getToken();
      const { data } = await axios.patch(
        `${url.baseUrl}/profile/${_id}`,
        { location },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      dispatch(getUserProfile()); // Get profile after updating profile
      return data;
    } catch (error: any) {
      console.log(error.response);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    // Collect inputs from the profile field
    collectInputs: (
      state,
      { payload }: PayloadAction<{ name: keyof ProfileState; value: any }>
    ) => {
      const { name, value } = payload;
      state.userProfile = { ...state.userProfile, [name]: value };
    },
  },

  extraReducers: (builder) => {
    // Get current user profile when the page loads
    builder
      .addCase(getUserProfile.pending, (state) => {
        state.profileLoading = true;
        // Start a timer to show toast message if request is still pending after 10 seconds
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
        }
        state.pendingRequestTimer = setTimeout(delayMessage, 10000);
      })
      .addCase(getUserProfile.fulfilled, (state, { payload }: any) => {
        state.profileLoading = false;
        // state.userProfile = payload; // Set the current state to the data in firestore
        state.userProfile = {
          ...state.userProfile,
          ...payload.data.profile,
        };
        // Clear timeout
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
          state.pendingRequestTimer = null; // Reset the timer ID
        }
      })
      .addCase(getUserProfile.rejected, (state, { payload }: any) => {
        state.profileLoading = false;
        serverMessage(payload);
        // Clear timeout
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
          state.pendingRequestTimer = null; // Reset the timer ID
        }
      });

    // Update data
    builder
      .addCase(updateProfile.pending, (state) => {
        state.profileLoading = true;
        // Start a timer to show toast message if request is still pending after 10 seconds
        state.pendingRequestTimer = setTimeout(delayMessage, 10000);
      })
      .addCase(updateProfile.fulfilled, (state, { payload }) => {
        state.profileLoading = false;
        toast.success(payload.data.message);
        // Clear timeout
        state.pendingRequestTimer && clearTimeout(state.pendingRequestTimer);
      })
      .addCase(updateProfile.rejected, (state, { payload }: any) => {
        state.profileLoading = true;
        serverMessage(payload);
        // Clear timeout
        state.pendingRequestTimer && clearTimeout(state.pendingRequestTimer);
      });
  },
});

export const { collectInputs } = profileSlice.actions;

export default profileSlice.reducer;
